import moment from "moment";
import "moment/locale/es";
import { React, useState, useEffect } from "react";
import AppWrapper from "../../AppWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../../supabase-client";
import PaymentClabe from "../../CustomComponents/PaymentClabe";
import {
  Button,
  FormLayout,
  Page,
  Select,
  TextField,
  Card,
  Text,
  Banner,
  InlineGrid,
  BlockStack,
  IndexTable,
  Badge,
  InlineStack,
} from "@shopify/polaris";
import { DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { formatDate, formatPrice } from "../../../utility/funcs";
import PopUpSearch from "../../CustomComponents/PopUpSearch/PopUpSearch";
import ClientCreditCard from "../../CustomComponents/ClientCredit/ClientCreditCard";
import ClientDocuments from "../../CustomComponents/ClientDocuments/ClientDocuments";
import ClientBenefits from "../../CustomComponents/ClientBenefits/ClientBenefits";
import useClientBlock from "../../CustomComponents/ClientBlock/useClientBlock";

const API_URL = "https://api.gomoverent.com/v2/alegra/proxy.php";
const API_TOKEN = "h8ISmsSIwTvfl8Tny2nr0dFh2nz3rmiCzBMjtCJOJqn7TF6Y4wnch7uI1FI3ArlZP0wfMsGkEuqNXUzaX9wYSL5bnn9CzB1DkWAMFQVCBWBhRwAXn29M96YYxcp1bmfN";

const SAT_TO_ALEGRA_13 = {
  "605": "SALARIED_REGIME",
  "606": "LEASEHOLD_REGIME",
  "608": "SALARIED_REGIME",
  "611": "DIVIDEND_INCOME",
  "612": "BUSINESS_ACTIVITIES_REGIME",
  "614": "SALARIED_REGIME",
  "616": "SIMPLIFIED_REGIME",
  "621": "FISCAL_INCORPORATION_REGIME",
  "625": "REGIME_OF_THE_TECHNOLOGICAL_PLATFORMS_INCOME_ACTIVITIES",
  "626": "REGIME_OF_TRUST",
};

// Country codes for the WhatsApp-capable phone. Mexico first (default).
const PHONE_COUNTRY_CODES = [
  { label: "🇲🇽 +52", value: "+52", len: 10 },
  { label: "🇺🇸 +1", value: "+1", len: 10 },
  { label: "🇪🇸 +34", value: "+34", len: 9 },
  { label: "🇨🇴 +57", value: "+57", len: 10 },
  { label: "🇦🇷 +54", value: "+54", len: 10 },
];

// Expected national-number length for a given country code (default 10).
const phoneLenFor = (code) =>
  PHONE_COUNTRY_CODES.find((c) => c.value === code)?.len || 10;

function CustomerEdit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchId = searchParams.get("id");
  // Existing customers open read-only; creating one starts in edit.
  const [isEditing, setIsEditing] = useState(!searchParams.get("id"));
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [customer, setCustomer] = useState({
    id: undefined,
    created_at: undefined,
    first_name: undefined,
    last_name: undefined,
    dob: undefined,
    email: undefined,
    phone_country_code: "+52",
    phone: undefined,
    phone2: undefined,
    license_number: undefined,
    license_state: "Nuevo León",
    license_expiration: undefined,
    id_number: undefined,
    id_expiration: undefined,
    id_type: "INE",
    rfc: undefined,
    razon_social: undefined,
    regimen_fiscal: undefined,
    codigo_postal_fiscal: undefined,
    notes: undefined,
    bank_name: "",
    bank_clabe: undefined,
    bank_holder_name: undefined,
    curp: undefined,
    rfc_pc: undefined,
  });
  const [reservationHistory, setReservationHistory] = useState([]);
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [companySearchOpen, setCompanySearchOpen] = useState(false);

  const fetchLinkedCompanies = async (customerId) => {
    if (!customerId) return;
    const { data } = await supabase
      .from("company_contacts")
      .select("id, is_primary, role, company:companies(id, name, rfc)")
      .eq("customer_id", customerId)
      .order("id");
    setLinkedCompanies(data || []);
  };

  const addCompanyLink = async ({ id }) => {
    if (!searchId) return;
    const alreadyLinked = linkedCompanies.some((c) => c.company?.id === id);
    if (alreadyLinked) {
      setCompanySearchOpen(false);
      return;
    }
    // Only make primary if the company has no existing contacts
    const { count } = await supabase
      .from("company_contacts")
      .select("id", { count: "exact", head: true })
      .eq("company_id", id);
    const makePrimary = (count || 0) === 0;
    const { error } = await supabase.from("company_contacts").insert({
      company_id: id,
      customer_id: parseInt(searchId),
      is_primary: makePrimary,
    });
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    if (makePrimary) {
      const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
      await supabase
        .from("companies")
        .update({
          contact_name: fullName,
          contact_phone: customer.phone || "",
          contact_email: customer.email || "",
        })
        .eq("id", id);
    }
    await fetchLinkedCompanies(searchId);
    setCompanySearchOpen(false);
  };

  const removeCompanyLink = async (linkId, wasPrimary, companyId) => {
    await supabase.from("company_contacts").delete().eq("id", linkId);
    if (wasPrimary && companyId) {
      const { data: remaining } = await supabase
        .from("company_contacts")
        .select("id, customer:customers(first_name, last_name, phone, email)")
        .eq("company_id", companyId)
        .order("id")
        .limit(1);
      if (remaining && remaining.length > 0) {
        const next = remaining[0];
        const n = `${next.customer?.first_name || ""} ${next.customer?.last_name || ""}`.trim();
        await supabase
          .from("company_contacts")
          .update({ is_primary: true })
          .eq("id", next.id);
        await supabase
          .from("companies")
          .update({
            contact_name: n,
            contact_phone: next.customer?.phone || "",
            contact_email: next.customer?.email || "",
          })
          .eq("id", companyId);
      } else {
        await supabase
          .from("companies")
          .update({ contact_name: "", contact_phone: "", contact_email: "" })
          .eq("id", companyId);
      }
    }
    await fetchLinkedCompanies(searchId);
  };

  const stateList = [
    "Nuevo León",
    "Aguascalientes",
    "Baja California",
    "Baja California Sur",
    "Campeche",
    "Chiapas",
    "Chihuahua",
    "Ciudad de México",
    "Coahuila",
    "Colima",
    "Durango",
    "Estado de México",
    "Guanajuato",
    "Guerrero",
    "Hidalgo",
    "Jalisco",
    "Michoacán",
    "Morelos",
    "Nayarit",
    "Oaxaca",
    "Puebla",
    "Querétaro",
    "Quintana Roo",
    "San Luis Potosí",
    "Sinaloa",
    "Sonora",
    "Tabasco",
    "Tamaulipas",
    "Tlaxcala",
    "Veracruz",
    "Yucatán",
    "Zacatecas",
    "Internacional",
  ];
  const banks = [
    "BANORTE",
    "ABC CAPITAL",
    "ACCIVAL",
    "ACTINVER",
    "AFIRME",
    "AKALA",
    "AMERICAN EXPRESS",
    "ASEA",
    "AUTOFIN",
    "AZTECA",
    "BAJIO",
    "BAMSA",
    "BANAMEX",
    "BANCO FAMSA",
    "BANCOMEXT",
    "BANCOOPPEL",
    "BANJERCITO",
    "BANKAOOL", // no está en el PDF pero si se llegara a agregar
    "BANOBRAS",
    "BANREGIO",
    "BANSI",
    "BARCLAYS",
    "BASE",
    "BBVA BANCOMER",
    "B&B",
    "BMULTIVA",
    "BMONEX",
    "BULLTICK CB",
    "CB ACTINVER",
    "CB INTERCAM",
    "CBDEUTSCHE",
    "CB JPMORGAN",
    "CIBANCO",
    "CI BOLSA",
    "CLS",
    "COMPARTAMOS",
    "CONSUBANCO",
    "CREDIT SUISSE",
    "DEUTSCHE",
    "EAST WEST BANK", // tampoco está pero aparece en otros catálogos oficiales
    "EVERCORE",
    "ESTRUCTURADORES",
    "FINAMEX",
    "FINCOMUN",
    "GBM",
    "HDI SEGUROS",
    "HIPOTECARIA FEDERAL",
    "HSBC",
    "INBURSA",
    "INDEVAL",
    "ING",
    "INTERACCIONES",
    "INTERBANCO",
    "INVEX",
    "IXE",
    "JP MORGAN",
    "KUSPIT",
    "LIBERTAD",
    "MAPFRE",
    "MASARI",
    "MERCADO PAGO W",
    "MERRILL LYNCH",
    "MIFEL",
    "MONEXCB",
    "NAFIN",
    "OACTIN",
    "OPCIONES EMPRESARIALES DEL NOROESTE",
    "ORDER",
    "PROFUTURO",
    "REFORMA",
    "SCOTIABANK",
    "SEGMTY",
    "SKANDIA",
    "SOFIEXPRESS",
    "STP",
    "STERLING",
    "SU CASITA",
    "SANTANDER",
    "TELECOMM",
    "THE ROYAL BANK",
    "TIBER",
    "TOKYO",
    "UNAGRA",
    "UNICA",
    "VALMEX",
    "VALUE",
    "VECTOR",
    "VE POR MAS",
    "VOLKSWAGEN",
    "WAL-MART",
    "ZURICH",
    "ZURICHVI",
  ];

  // Default to "Seleccionar…" so an empty bank doesn't silently display as the
  // first entry in the list (BANORTE). If a stored value isn't in our list
  // (legacy / free-text), keep it as an option so it displays and isn't lost.
  // Mirrors the same treatment in CompanyEdit.
  const bankOptions = [
    { value: "", label: "Seleccionar…" },
    ...(customer.bank_name && !banks.includes(customer.bank_name)
      ? [{ value: customer.bank_name, label: customer.bank_name }]
      : []),
    ...banks.map((b) => ({ value: b, label: b })),
  ];


  // Read-only view. Same shape as the company page: label above value so the
  // two stay adjacent, two columns so twelve facts do not need a page and a
  // half of scrolling.
  const infoRow = (label, value) => (
    <BlockStack gap="050" key={label}>
      <Text tone="subdued" variant="bodySm">{label}</Text>
      <Text breakWord>{value || "—"}</Text>
    </BlockStack>
  );

  const infoCard = (title, rows, extra) => (
    <Card>
      <BlockStack gap={300}>
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm" as="h3">{title}</Text>
          {extra}
        </InlineStack>
        <InlineGrid columns={{ xs: 1, sm: 2 }} gap={300}>{rows}</InlineGrid>
      </BlockStack>
    </Card>
  );

  // The licence is the one field here with an operational deadline — an expired
  // one already raises a warning on the reservation page — so its state is
  // shown, not left for someone to work out from a date.
  const licenceBadge = () => {
    if (!customer.license_expiration) return null;
    const exp = moment(customer.license_expiration, "YYYY-MM-DD");
    const days = exp.diff(moment(), "days");
    if (days < 0) return <Badge tone="critical">Vencida</Badge>;
    if (days <= 30) return <Badge tone="warning">Vence en {days} días</Badge>;
    return <Badge tone="success">Vigente</Badge>;
  };

  const fmtDate = (d) => (d ? moment(d, "YYYY-MM-DD").format("D [de] MMM YYYY") : null);

  const hasFiscalData = !!(
    customer.rfc || customer.razon_social || customer.regimen_fiscal || customer.codigo_postal_fiscal
  );

  const customerReadView = () => (
    <BlockStack gap={400}>
      <InlineGrid columns={{ xs: 1, md: ["twoThirds", "oneThird"] }} gap={400}>
        {infoCard("Datos personales", [
          infoRow("Nombre", `${customer.first_name || ""} ${customer.last_name || ""}`.trim()),
          infoRow("Fecha de nacimiento", fmtDate(customer.dob)),
          infoRow("CURP", customer.curp),
        ])}
        {searchId ? (
          <PaymentClabe
            ownerType="customer"
            ownerId={parseInt(searchId)}
            ownerName={`${customer.first_name || ""} ${customer.last_name || ""}`.trim()}
          />
        ) : null}
      </InlineGrid>

      <InlineGrid columns={{ xs: 1, md: 2 }} gap={400}>
        {infoCard("Contacto", [
          infoRow("Celular", customer.phone),
          infoRow("Teléfono fijo", customer.phone2),
          infoRow("Correo", customer.email),
        ])}
        {infoCard("Licencia", [
          infoRow("Número", customer.license_number),
          infoRow("Emitida en", customer.license_state),
          infoRow("Vence", fmtDate(customer.license_expiration)),
        ], licenceBadge())}
      </InlineGrid>

      <InlineGrid columns={{ xs: 1, md: 2 }} gap={400}>
        {infoCard("Identificación", [
          infoRow("Tipo", customer.id_type),
          infoRow("Número", customer.id_number),
          infoRow("Vence", customer.id_expiration),
        ])}
        {infoCard("Cuenta del cliente (para devoluciones)", [
          infoRow("Banco", customer.bank_name),
          infoRow("CLABE", customer.bank_clabe),
          infoRow("Titular", customer.bank_holder_name),
        ])}
      </InlineGrid>

      {/* Only when they actually invoice: 30% of active renters have no RFC and
          93% no razón social, so an always-empty card would be noise on a third
          of the people in the system. */}
      {hasFiscalData &&
        infoCard("Facturación", [
          infoRow("RFC", customer.rfc),
          infoRow("Razón social", customer.razon_social),
          infoRow("Régimen fiscal", customer.regimen_fiscal),
          infoRow("C.P. fiscal", customer.codigo_postal_fiscal),
        ])}

      {customer.notes ? infoCard("Notas", [infoRow("", customer.notes)]) : null}
    </BlockStack>
  );

  const cancelEditing = () => {
    if (savedSnapshot) setCustomer(savedSnapshot);
    setIsEditing(false);
  };

  const saveCustomerInfo = async (e) => {
    // Defensive: the Save button is disabled when required fields are missing,
    // but guard here too. (Phone needs 10 digits; rest just non-empty.)
    if (!canSave) {
      alert("Completa los campos obligatorios (marcados con *) antes de guardar.");
      return;
    }
    // "" is the Select's no-bank sentinel; persist it as NULL so customers.bank_name
    // stays uniformly null rather than a mix of null and '' (companies has both).
    const payload = { ...customer, bank_name: customer.bank_name || null };

    const { error: saveError } = searchId
      ? await supabase.from("customers").update(payload).eq("id", searchId)
      : await supabase.from("customers").insert(payload);
    if (saveError) {
      // Stop here: syncing Alegra with data the DB rejected would push this
      // form's RFC onto whichever contact already owns it.
      let msg = saveError.message;
      if (saveError.code === "23505" && /curp/i.test(saveError.message)) {
        msg = `La CURP ${customer.curp} ya está registrada en otro cliente.`;
      }
      alert("No se guardó el cliente:\n" + msg);
      return;
    }
    // Sync billing data to Alegra if RFC is real and regime is set
    const rfc = customer.rfc;
    if (rfc && rfc !== "XAXX010101000" && customer.regimen_fiscal) {
      try {
        const alegraRegime = SAT_TO_ALEGRA_13[customer.regimen_fiscal] || "SIMPLIFIED_REGIME";
        const contactName = (customer.razon_social || `${customer.first_name} ${customer.last_name}`).toUpperCase();
        const searchRes = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
          body: JSON.stringify({ endpoint: `contacts?identification=${rfc}`, method: "GET" }),
        });
        const searchData = await searchRes.json();
        const existing = searchData.success && Array.isArray(searchData.data) && searchData.data.length > 0 ? searchData.data[0] : null;

        const contactBody = {
          name: contactName,
          identification: rfc,
          // Alegra requires the `regime` string (not just regimeObject) on
          // invoicing-enabled accounts, else the write is rejected with 2141
          // "El régimen es obligatorio". Send both, kept in sync.
          regime: alegraRegime,
          regimeObject: [alegraRegime],
          address: { zipCode: customer.codigo_postal_fiscal || "", country: "MEX" },
        };

        if (existing) {
          const putRes = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
            body: JSON.stringify({ endpoint: `contacts/${existing.id}`, method: "PUT", body: contactBody }),
          });
          const putData = await putRes.json();
          if (!putData.success) {
            throw new Error(putData.error?.message || putData.error || "No se pudo actualizar el contacto en Alegra");
          }
          if (searchId && !customer.alegra_contact_id) {
            await supabase.from("customers").update({ alegra_contact_id: String(existing.id) }).eq("id", searchId);
          }
        } else {
          contactBody.type = ["client"];
          contactBody.thirdType = "NATIONAL";
          const createRes = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
            body: JSON.stringify({ endpoint: "contacts", method: "POST", body: contactBody }),
          });
          const createData = await createRes.json();
          if (!createData.success) {
            throw new Error(createData.error?.message || createData.error || "No se pudo crear el contacto en Alegra");
          }
          if (createData.data?.id && searchId) {
            await supabase.from("customers").update({ alegra_contact_id: String(createData.data.id) }).eq("id", searchId);
          }
        }
      } catch (e) {
        // The customer record saved fine; only the Alegra sync failed. Surface
        // it so the fiscal contact doesn't silently drift out of sync (which
        // surfaces as "el nombre es incorrecto" at stamping time).
        alert(
          "El cliente se guardó, pero falló la sincronización fiscal en Alegra:\n" +
            (e?.message || e) +
            "\n\nCorrige y vuelve a guardar antes de facturar."
        );
      }
    }

    navigate("/customers");
  };

  const handleInformation = (value, id) => {
    switch (id) {
      case "first_name":
      case "last_name":
      case "bank_holder_name":
        value = value
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        break;
      case "rfc":
        value = value.toUpperCase();
        value = value.substring(0, 13);
        break;
      case "phone":
        // Keep only digits — the country code is stored separately, and the
        // bare national number is what WhatsApp/E.164 needs. Cap to the
        // expected length for the selected country (10 for MX/US, 9 for ES…).
        value = value.replace(/\D/g, "").substring(0, phoneLenFor(customer.phone_country_code));
        break;
      case "bank_clabe":
        value = value.replace(/\D/g, "");
        value = value.substring(0, 18);
        value = `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(
          6,
          17
        )} ${value.slice(17)}`;
        value = value.trim();
        break;

      default:
        break;
    }
    setCustomer({ ...customer, [id]: value });
  };

  useEffect(() => {
    const getCustomer = async () => {
      const { data, error } = await supabase
        .from("customers")
        .select()
        .eq("id", searchId)
        .limit(1)
        .single();
      if (error) {
        console.error(error.data);
        return { success: false, error: error.message };
      }
      setCustomer(data);
      setSavedSnapshot(data);
    };
    const getReservationHistory = async () => {
      const { data } = await supabase
        .from("reservations")
        .select("id, date_range, total_price, status, fully_paid, car(plate, model(name))")
        .eq("customer", searchId)
        .eq("type", "rental")
        .order("created_at", { ascending: false });
      if (data) setReservationHistory(data);
    };

    if (searchId) {
      getCustomer();
      getReservationHistory();
      fetchLinkedCompanies(searchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId]);

  // Fields the UI requires before a customer can be saved (red asterisk +
  // disabled Save). Enforced in the app, not the DB.
  const REQUIRED_FIELDS = [
    "first_name",
    "last_name",
    "dob",
    "phone",
    "email",
    "license_number",
    "license_expiration",
    "id_type",
    "id_number",
    "id_expiration",
  ];
  const isRequiredFilled = (key) => {
    const v = (customer[key] ?? "").toString().trim();
    if (key === "phone")
      return v.replace(/\D/g, "").length === phoneLenFor(customer.phone_country_code);
    return v !== "";
  };
  const missingRequired = REQUIRED_FIELDS.filter((k) => !isRequiredFilled(k));

  // Blocking on all ten made 744 of 914 existing customers unsavable: open one
  // to fix a phone number and Guardar stays greyed out until you supply a
  // licence and an ID expiry you may not have. So the rule applies where it
  // can be met — a NEW customer, where you have the person in front of you —
  // and an existing record can be corrected with what it has, with the gaps
  // named rather than silently enforced.
  const canSave = searchId ? true : missingRequired.length === 0;

  const REQUIRED_LABELS = {
    first_name: "Nombre(s)", last_name: "Apellido(s)", dob: "Fecha de nacimiento",
    phone: "Celular", email: "Correo", license_number: "No. de licencia",
    license_expiration: "Vencimiento de licencia", id_type: "Tipo de identificación",
    id_number: "No. de identificación", id_expiration: "Vencimiento de identificación",
  };

  const clientBlock = useClientBlock({
    entityType: "customer",
    entityId: searchId ? parseInt(searchId) : null,
    name: `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
    // Guardar writes the whole `customer` row back, so the local copy has to
    // learn about the block or it would revert it.
    onChange: (patch) => setCustomer((prev) => ({ ...prev, ...patch })),
  });

  return (
    <AppWrapper>
      <Page
        title={
          !searchId
            ? "Crear Cliente"
            : `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Cliente"
        }
        backAction={{ url: "/customers", content: "Clientes" }}
        actionGroups={clientBlock.actionGroups}
        primaryAction={
          // wrap=false: the Page header is narrow enough that Cancelar and
          // Guardar otherwise stack on top of each other.
          isEditing ? (
            <InlineStack gap={200} wrap={false} blockAlign="center">
              {searchId && (
                <Button onClick={cancelEditing}>Cancelar</Button>
              )}
              <Button
                variant="primary"
                onClick={saveCustomerInfo}
                disabled={!canSave || clientBlock.readOnly}
              >
                Guardar
              </Button>
            </InlineStack>
          ) : (
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              disabled={clientBlock.readOnly}
            >
              Editar
            </Button>
          )
        }
      >
        {clientBlock.markup}
        {/* A native disabled fieldset switches off every nested control at once,
            so a blocked client is read-only without touching each field. */}
        <fieldset
          disabled={clientBlock.readOnly}
          style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
        >
        {searchId && (
          <ClientCreditCard entityType="customer" entityId={parseInt(searchId)} />
        )}
        {isEditing && searchId && missingRequired.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <Banner tone="warning" title="Faltan datos obligatorios">
              <p>
                {missingRequired.map((k) => REQUIRED_LABELS[k] || k).join(", ")}.
                Puedes guardar sin ellos, pero conviene completarlos.
              </p>
            </Banner>
          </div>
        )}
        {isEditing ? (
        <FormLayout>
          <FormLayout.Group title="Datos Personales">
            <TextField
              label="Nombre(s)"
              value={customer.first_name}
              id="first_name"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Apellido(s)"
              value={customer.last_name}
              id="last_name"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Fecha de Nacimiento"
              value={customer.dob}
              id="dob"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              type="date"
            />
            <TextField
              label="CURP"
              value={customer.curp}
              id="curp"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Contacto">
            <TextField
              label="Celular"
              value={customer.phone}
              id="phone"
              type="tel"
              inputMode="numeric"
              maxLength={phoneLenFor(customer.phone_country_code)}
              requiredIndicator
              connectedLeft={
                <Select
                  label="Código país"
                  labelHidden
                  options={PHONE_COUNTRY_CODES}
                  value={customer.phone_country_code || "+52"}
                  onChange={(value) => handleInformation(value, "phone_country_code")}
                />
              }
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Telefono Fijo"
              value={customer.phone2}
              id="phone2"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Correo Electronico"
              value={customer.email}
              id="email"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Licencia">
            <TextField
              label="No. de Licencia"
              value={customer.license_number}
              id="license_number"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <Select
              label="Emitida en"
              placeholder="Seleccionar"
              value={customer.license_state || ""}
              id="license_state"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              options={stateList}
            />
            <TextField
              label="Fecha de Vencimiento"
              value={customer.license_expiration}
              id="license_expiration"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              type="date"
            />
          </FormLayout.Group>
          <FormLayout.Group title="Identificación">
            <Select
              label="Tipo"
              // placeholder renders a blank option when the stored value is
              // empty. Without it the browser shows the FIRST option as if it
              // were selected while state holds nothing — the field looks set,
              // is not, and (when required) Guardar stays disabled until you
              // change the value and change it back.
              placeholder="Seleccionar"
              value={customer.id_type || ""}
              id="id_type"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              options={["INE", "Pasaporte", "Otro"]}
            />
            <TextField
              label="No. de Identificacion"
              value={customer.id_number}
              id="id_number"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Año de Vencimiento"
              value={customer.id_expiration}
              id="id_expiration"
              requiredIndicator
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
          </FormLayout.Group>
          {/* Their account — where WE send deposit refunds. Distinct from the
              CLABE below, which is ours, for them to pay into. */}
          <FormLayout.Group title="Cuenta del cliente (para devoluciones)">
            <Select
              label="Banco"
              value={customer.bank_name || ""}
              id="bank_name"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              options={bankOptions}
            />
            <TextField
              label="Titular"
              value={customer.bank_holder_name}
              id="bank_holder_name"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="CLABE del cliente"
              helpText="Su cuenta, para devolverle depósitos."
              value={customer.bank_clabe}
              id="bank_clabe"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Facturación">
            <TextField
              label="RFC"
              value={customer.rfc}
              id="rfc"
              onChange={(value, id) => {
                handleInformation(value.toUpperCase(), id);
              }}
            />
            <TextField
              label="Razón Social"
              value={customer.razon_social}
              id="razon_social"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              placeholder="Si difiere del nombre del cliente"
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Select
              label="Régimen Fiscal"
              value={customer.regimen_fiscal || ""}
              id="regimen_fiscal"
              options={[
                { label: "Seleccionar", value: "" },
                { label: "605 — Sueldos y Salarios e Ingresos Asimilados", value: "605" },
                { label: "606 — Arrendamiento", value: "606" },
                { label: "608 — Demás ingresos", value: "608" },
                { label: "611 — Ingresos por Dividendos", value: "611" },
                { label: "612 — Personas Físicas con Actividades Empresariales y Profesionales", value: "612" },
                { label: "614 — Ingresos por intereses", value: "614" },
                { label: "616 — Sin obligaciones fiscales", value: "616" },
                { label: "621 — Incorporación Fiscal", value: "621" },
                { label: "625 — Plataformas Tecnológicas", value: "625" },
                { label: "626 — Régimen Simplificado de Confianza (RESICO)", value: "626" },
              ]}
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
            />
            <TextField
              label="Código Postal Fiscal"
              value={customer.codigo_postal_fiscal}
              id="codigo_postal_fiscal"
              onChange={(value, id) => {
                handleInformation(value.replace(/\D/g, "").substring(0, 5), id);
              }}
              maxLength={5}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label="Notas"
              value={customer.notes}
              id="notes"
              onChange={(value, id) => {
                handleInformation(value, id);
              }}
              multiline
            />
          </FormLayout.Group>
        </FormLayout>
        ) : (
          customerReadView()
        )}
        <div style={{ marginTop: "2rem" }}>
          <ClientDocuments
            entityType="customer"
            entityId={searchId ? parseInt(searchId) : null}
          />
        </div>
        <div style={{ marginTop: "2rem" }}>
          <ClientBenefits
            entityType="customer"
            entityId={searchId ? parseInt(searchId) : null}
          />
        </div>
        {searchId && (
          <div style={{ marginTop: "2rem" }}>
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd">
                    Empresas vinculadas ({linkedCompanies.length})
                  </Text>
                  <Button
                    icon={PlusIcon}
                    onClick={() => setCompanySearchOpen(true)}
                    size="slim"
                  >
                    Vincular empresa
                  </Button>
                </InlineStack>
                {linkedCompanies.length === 0 ? (
                  <Text tone="subdued">No hay empresas vinculadas.</Text>
                ) : (
                  <BlockStack gap="200">
                    {linkedCompanies.map((l) => (
                      <InlineStack
                        key={l.id}
                        align="space-between"
                        blockAlign="center"
                        gap="200"
                      >
                        <BlockStack gap="050">
                          <InlineStack gap="200" blockAlign="center">
                            <Text
                              variant="bodyMd"
                              fontWeight="semibold"
                              onClick={() => navigate(`/companies/edit?id=${l.company?.id}`)}
                              style={{ cursor: "pointer" }}
                            >
                              {l.company?.name}
                            </Text>
                            {l.is_primary && <Badge tone="success">Principal</Badge>}
                          </InlineStack>
                          {l.company?.rfc && (
                            <Text variant="bodySm" tone="subdued">
                              RFC: {l.company.rfc}
                            </Text>
                          )}
                        </BlockStack>
                        <Button
                          icon={DeleteIcon}
                          variant="plain"
                          tone="critical"
                          onClick={() => removeCompanyLink(l.id, l.is_primary, l.company?.id)}
                          accessibilityLabel="Quitar vínculo"
                        />
                      </InlineStack>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </div>
        )}
        {searchId && reservationHistory.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">
                  Historial de Reservaciones ({reservationHistory.length})
                </Text>
                <IndexTable
                  resourceName={{ singular: "Reserva", plural: "Reservas" }}
                  selectable={false}
                  itemCount={reservationHistory.length}
                  headings={[
                    { title: "#" },
                    { title: "Auto" },
                    { title: "Total" },
                    { title: "Estado" },
                  ]}
                >
                  {reservationHistory.map((res, index) => (
                    <IndexTable.Row
                      id={res.id}
                      key={res.id}
                      position={index}
                      onClick={() => navigate(`/reservation?id=${res.id}`)}
                    >
                      <IndexTable.Cell>
                        <Text fontWeight="semibold">#{res.id}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {res.car ? `${res.car.model?.name || ""} (${res.car.plate || ""})` : "—"}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {formatPrice(res.total_price)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {res.status === "cancelled" ? (
                          <Badge tone="critical">Cancelada</Badge>
                        ) : res.status === "completed" ? (
                          <Badge>Completada</Badge>
                        ) : res.fully_paid ? (
                          <Badge tone="success">Pagada</Badge>
                        ) : (
                          <Badge tone="attention">Pendiente</Badge>
                        )}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </BlockStack>
            </Card>
          </div>
        )}
        <PopUpSearch
          activated={companySearchOpen}
          setOpen={setCompanySearchOpen}
          getCustomer={({ id }) => addCompanyLink({ id })}
          title="Vincular Empresa"
          dbFunction="search_company"
        />
        </fieldset>
      </Page>
    </AppWrapper>
  );
}

export default CustomerEdit;
